import React, { useEffect, useState } from "react";

import { createAdminFarmOrganicFertilizerProduction, deleteAdminFarmOrganicFertilizerProduction, updateAdminFarmOrganicFertilizerProduction } from "services";

import { generateRandomId } from "utils";

import { ANIMAL_BASED_FERTILIZER_SLUGS, BIOMASS_INPUT_TYPE_OPTIONS, QUANTITY_UNIT_OPTIONS } from "consts";

import { CooperationItemRequest, FarmOrganicFertilizerProductionEntry, OwnProductionItemRequest, QuantityUnit } from "models";

import {
  Button,
  Drawer,
  Flex,
  Form,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  Link,
  Option,
  Select,
  Switch,
  TextArea,
  Typography,
} from "components";

// ─── Types ────────────────────────────────────────────────────────────────────

type OwnProductionItemDraft = {
  tempId: string;
  biomassInputType: string | null;
  quantity: number | null;
  quantityUnit: QuantityUnit | null;
  comment: string | null;
};

type OwnProductionValues = {
  enabled: boolean;
  livestockCount: number | null;
  comment: string | null;
  items: OwnProductionItemDraft[];
};

type CoopItemDraft = {
  tempId: string;
  exportedBiomassType: string | null;
  quantity: number | null;
  quantityUnit: QuantityUnit | null;
};

type CoopValues = {
  enabled: boolean;
  items: CoopItemDraft[];
};

type FormValues = {
  ownProduction: OwnProductionValues;
  cooperation: CoopValues;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const newOwnItem = (): OwnProductionItemDraft => ({
  tempId: generateRandomId(),
  biomassInputType: null,
  quantity: null,
  quantityUnit: null,
  comment: null,
});

const newCoopItem = (): CoopItemDraft => ({
  tempId: generateRandomId(),
  exportedBiomassType: null,
  quantity: null,
  quantityUnit: null,
});

function initFromRecords(records: FarmOrganicFertilizerProductionEntry[]): FormValues {
  const ownRecord = records.find((r) => r.sourceType === "own_production");
  const coopRecord = records.find((r) => r.sourceType === "cooperation");
  return {
    ownProduction: ownRecord
      ? {
          enabled: true,
          livestockCount: ownRecord.livestockCount,
          comment: ownRecord.comment,
          items: ownRecord.ownProductionItems.map((i) => ({
            tempId: i.id,
            biomassInputType: i.biomassInputType,
            quantity: i.quantity,
            quantityUnit: i.quantityUnit,
            comment: i.comment,
          })),
        }
      : { enabled: false, livestockCount: null, comment: null, items: [] },
    cooperation: coopRecord
      ? {
          enabled: true,
          items: coopRecord.cooperationItems.length
            ? coopRecord.cooperationItems.map((i) => ({
                tempId: i.id,
                exportedBiomassType: i.exportedBiomassType,
                quantity: i.quantity,
                quantityUnit: i.quantityUnit,
              }))
            : [newCoopItem()],
        }
      : { enabled: false, items: [] },
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

type Props = {
  visible: boolean;
  farmId: string;
  year: number;
  fertilizerId: string;
  fertilizerName: string;
  fertilizerSlug: string;
  existingRecords: FarmOrganicFertilizerProductionEntry[];
  onClose: () => void;
  createProduction: typeof createAdminFarmOrganicFertilizerProduction;
  updateProduction: typeof updateAdminFarmOrganicFertilizerProduction;
  deleteProduction: typeof deleteAdminFarmOrganicFertilizerProduction;
};

// ─── Component ────────────────────────────────────────────────────────────────

export const AdminOrganicFertilizerSourcesDrawer = ({
  visible,
  farmId,
  year,
  fertilizerId,
  fertilizerName,
  fertilizerSlug,
  existingRecords,
  onClose,
  createProduction,
  updateProduction,
  deleteProduction,
}: Props) => {
  const isAnimalBased = ANIMAL_BASED_FERTILIZER_SLUGS.has(fertilizerSlug);
  const [values, setValues] = useState<FormValues>(() => initFromRecords(existingRecords));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setValues(initFromRecords(existingRecords));
  }, [existingRecords]);

  const { ownProduction, cooperation } = values;

  const patchOwn = (patch: Partial<OwnProductionValues>) => setValues((v) => ({ ...v, ownProduction: { ...v.ownProduction, ...patch } }));
  const patchCoop = (patch: Partial<CoopValues>) => setValues((v) => ({ ...v, cooperation: { ...v.cooperation, ...patch } }));

  const updateOwnItem = (tempId: string, patch: Partial<OwnProductionItemDraft>) =>
    patchOwn({ items: ownProduction.items.map((item) => (item.tempId === tempId ? { ...item, ...patch } : item)) });

  const updateCoopItem = (tempId: string, patch: Partial<CoopItemDraft>) =>
    patchCoop({ items: cooperation.items.map((item) => (item.tempId === tempId ? { ...item, ...patch } : item)) });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const ownRecord = existingRecords.find((r) => r.sourceType === "own_production");
      const coopRecord = existingRecords.find((r) => r.sourceType === "cooperation");

      // own_production
      if (ownProduction.enabled) {
        const ownProductionItems: OwnProductionItemRequest[] = !isAnimalBased
          ? ownProduction.items
              .filter((i) => i.biomassInputType && i.quantity && i.quantityUnit)
              .map((i) => ({ biomassInputType: i.biomassInputType!, quantity: i.quantity!, quantityUnit: i.quantityUnit!, comment: i.comment }))
          : [];
        const payload = {
          farmId,
          sourceType: "own_production" as const,
          livestockCount: isAnimalBased ? ownProduction.livestockCount ?? undefined : undefined,
          comment: isAnimalBased ? ownProduction.comment : undefined,
          ownProductionItems: !isAnimalBased ? ownProductionItems : undefined,
        };
        if (ownRecord) await updateProduction({ ...payload, productionId: ownRecord.id, year });
        else await createProduction({ ...payload, year, fertilizerId });
      } else if (ownRecord) {
        await deleteProduction({ farmId, productionId: ownRecord.id, year });
      }

      // cooperation
      if (cooperation.enabled) {
        const cooperationItems: CooperationItemRequest[] = cooperation.items
          .filter((i) => i.exportedBiomassType && i.quantity && i.quantityUnit)
          .map((i) => ({ exportedBiomassType: i.exportedBiomassType!, quantity: i.quantity!, quantityUnit: i.quantityUnit! }));
        const payload = { farmId, sourceType: "cooperation" as const, cooperationItems };
        if (coopRecord) await updateProduction({ ...payload, productionId: coopRecord.id, year });
        else await createProduction({ ...payload, year, fertilizerId });
      } else if (coopRecord) {
        await deleteProduction({ farmId, productionId: coopRecord.id, year });
      }

      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      title={`Edit sources — ${fertilizerName}`}
      visible={visible}
      onClose={onClose}
      footer={
        <Flex gap="0.5rem">
          <Button type="secondary" block onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" block loading={isSaving} onClick={handleSave}>
            Save
          </Button>
        </Flex>
      }
    >
      <Form>
        <Flex vertical gap="1.5rem">
          {/* Own production */}
          <Flex vertical gap="0.75rem">
            <Flex align="center" justify="space-between">
              <Typography variant="paragraph-m" bold className="margin-0">
                From own production
              </Typography>
              <Switch
                checked={ownProduction.enabled}
                onChange={(enabled) =>
                  patchOwn({ enabled, items: enabled && !isAnimalBased && ownProduction.items.length === 0 ? [newOwnItem()] : ownProduction.items })
                }
              />
            </Flex>

            {ownProduction.enabled && (
              <Flex vertical gap="0.75rem">
                {isAnimalBased ? (
                  <>
                    <InputNumber
                      label="Livestock number"
                      value={ownProduction.livestockCount ?? undefined}
                      initialValue={ownProduction.livestockCount ?? undefined}
                      onChange={(val) => patchOwn({ livestockCount: val })}
                      min={1}
                      step={1}
                      decimalSeparator=","
                    />
                    <TextArea
                      name="animalComment"
                      label="Comment (optional)"
                      initialValue={ownProduction.comment}
                      onChange={(e) => patchOwn({ comment: e.target.value || null })}
                      autoSize={{ minRows: 2, maxRows: 2 }}
                    />
                  </>
                ) : (
                  <Flex vertical gap="0.75rem">
                    {ownProduction.items.map((item) => (
                      <Flex key={item.tempId} vertical gap="0.5rem">
                        <Flex align="center" gap="0.5rem">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Select
                              label="Biomass input type"
                              value={item.biomassInputType ?? null}
                              initialValue={item.biomassInputType ?? undefined}
                              onChange={(val: string | null) => updateOwnItem(item.tempId, { biomassInputType: val })}
                              allowClear
                            >
                              {BIOMASS_INPUT_TYPE_OPTIONS.map((opt) => (
                                <Option key={opt} value={opt} label={[opt]}>
                                  {opt}
                                </Option>
                              ))}
                            </Select>
                          </div>
                          <IconButton
                            iconName="tabler/trash"
                            onClick={() => patchOwn({ items: ownProduction.items.filter((i) => i.tempId !== item.tempId) })}
                          />
                        </Flex>
                        <InputNumberWithUnitSelector
                          label="Quantity"
                          name={`ownQty-${item.tempId}`}
                          value={item.quantity ?? undefined}
                          initialValue={item.quantity ?? undefined}
                          unitValue={item.quantityUnit}
                          units={[...QUANTITY_UNIT_OPTIONS]}
                          unitLabel="Unit"
                          min={0}
                          onChange={(val, unit) => updateOwnItem(item.tempId, { quantity: val, quantityUnit: unit as QuantityUnit | null })}
                        />
                        {item.biomassInputType === "other" && (
                          <TextArea
                            key={`ownComment-${item.tempId}`}
                            name={`ownComment-${item.tempId}`}
                            label="Comment"
                            initialValue={item.comment}
                            onChange={(e) => updateOwnItem(item.tempId, { comment: e.target.value || null })}
                            autoSize={{ minRows: 2, maxRows: 2 }}
                          />
                        )}
                      </Flex>
                    ))}
                    <Link onClick={() => patchOwn({ items: [...ownProduction.items, newOwnItem()] })}>
                      <Icon iconName="klim/plus" className="icon inline" /> Add biomass type
                    </Link>
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>

          {/* Cooperation */}
          <Flex vertical gap="0.75rem">
            <Flex align="center" justify="space-between">
              <Typography variant="paragraph-m" bold className="margin-0">
                From a farm cooperation
              </Typography>
              <Switch
                checked={cooperation.enabled}
                onChange={(enabled) => patchCoop({ enabled, items: enabled && cooperation.items.length === 0 ? [newCoopItem()] : cooperation.items })}
              />
            </Flex>

            {cooperation.enabled && (
              <Flex vertical gap="0.75rem">
                {cooperation.items.map((item) => (
                  <Flex key={item.tempId} vertical gap="0.5rem">
                    <Flex align="center" gap="0.5rem">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Select
                          label="Exported biomass type"
                          value={item.exportedBiomassType ?? null}
                          initialValue={item.exportedBiomassType ?? undefined}
                          onChange={(val: string | null) => updateCoopItem(item.tempId, { exportedBiomassType: val })}
                          allowClear
                        >
                          {BIOMASS_INPUT_TYPE_OPTIONS.map((opt) => (
                            <Option key={opt} value={opt} label={[opt]}>
                              {opt}
                            </Option>
                          ))}
                        </Select>
                      </div>
                      <IconButton iconName="tabler/trash" onClick={() => patchCoop({ items: cooperation.items.filter((i) => i.tempId !== item.tempId) })} />
                    </Flex>
                    <InputNumberWithUnitSelector
                      label="Quantity"
                      name={`coopQty-${item.tempId}`}
                      value={item.quantity ?? undefined}
                      initialValue={item.quantity ?? undefined}
                      unitValue={item.quantityUnit}
                      units={[...QUANTITY_UNIT_OPTIONS]}
                      unitLabel="Unit"
                      min={0}
                      onChange={(val, unit) => updateCoopItem(item.tempId, { quantity: val, quantityUnit: unit as QuantityUnit | null })}
                    />
                  </Flex>
                ))}
                <Link onClick={() => patchCoop({ items: [...cooperation.items, newCoopItem()] })}>
                  <Icon iconName="klim/plus" className="icon inline" /> Add biomass type
                </Link>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Form>
    </Drawer>
  );
};
